// Neu (SPEC 03 §6.3, E15) — Live-Aktualisierung über SSE mit Abfrage-Rückfall.
//
// Einmal in der Shell für das aktuelle Verfahren montiert:
//   · EventSource auf /api/v/{V}/live. `stand` {seq, ts, geaendert[]} macht die
//     betroffenen Abfragen ungültig (Präfix [familie, V]), `lauf` ersetzt
//     ["lauf", V] sofort (gleiche LaufStand-Form) und macht gedrosselt die
//     Lauf-Familien ungültig.
//   · Zwei Fehler hintereinander ohne `open` → Abfragebetrieb: alle 10 s
//     GET /stand?seit=seq; nach 5 min einmal wieder SSE versuchen.
//   · Der planmäßige Server-Schluss nach 540 s ist EIN onerror, EventSource
//     verbindet selbst neu, das folgende onopen setzt die Fehlerfolge zurück.
//   · `fehler` (Sitzung/Zugriff beendet) schließt und frischt ["ich"] auf.
//   · Verborgener Tab > 60 s schließt; beim Zurückkehren sofort /stand + neu.
//   · `auftraege` (Auftragsstände des Verfahrens geändert, serverseitig ≤ 1/s): macht
//     ["auftraege", V] sofort ungültig und gedrosselt (wie `lauf`) die Lauf-Familien samt
//     ["lauf", V] — Eingang und Lauf-Seite sehen den neuen Stand ohne eigenes Abfragen.
//   · Ortsverzeichnis → Auswertung: Katalog und Kacheln hängen am Verzeichnis (Facette
//     „Stadtteil“, Karten-Kachel). Ändert sich der Stand des Verzeichnisses — gleich, welcher
//     Bildschirm ihn holte — oder wird ein Auftrag „Ortsverzeichnis laden“ fertig (Aufträge-Stand
//     der Kopfleiste), werden ["katalog", V] und ["kacheln", V] ungültig; nach dem Import auch die
//     Familie „orte“ (Karte der Kachel). Kein eigener Abruf, nichts je Kodierung.
import { useEffect } from "react";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { holeStand, liveUrl } from "../api/client";
import type { AuftraegeStand, LaufStand, OrteStand, StandBereich, StandEreignis } from "../api/typen";
import { useStore } from "../store/useStore";

export const ABFRAGE_MS = 10_000;
export const SSE_WIEDER_MS = 5 * 60_000;
export const VERBORGEN_MS = 60_000;
export const LAUF_DROSSEL_MS = 5_000;

const DATEN_FAMILIEN = [
  "zaehlen",
  "kennzahlen",
  "bestand", // E34: Bestand der Antworten (Band der Auswertung)
  "facetten",
  "antworten",
  "zitate",
  "verfahren",
  "beleg",
  "seite",
  "bogen",
  "pruefen",
  "boegen",
  "boegen-vorschlaege",
  "stichprobe",
  "kodierung",
  "dokumente",
  "dokument",
  "masken",
  "kaestchen", // E23: Gütekarte im Lauf (Übereinstimmung mit geprüften Antworten, offene Abweichungen)
  // E21: verortung/ort/ortsbezirk melden sich serverseitig als „kodierung“ (05 §15.6).
  // Befund F21: NICHT die ganze Familie „orte“ (Karte mit bis 6000 Stützpunkten, Listen, Suchen)
  // — nur ["orte", V, "stand"] (orteStandAuffrischen); ändert sich der Stand des Verzeichnisses,
  // folgt die ganze Familie.
  "verortung", // Orte prüfen
] as const;
const THEMA_FAMILIEN = ["katalog", "kacheln", "themen", "thema"] as const;
// `kaestchen` (E23): der CPU-Auftrag „Kästchen messen“ meldet sich über `auftraege`.
// `orte` (E21): ein fertiger CPU-Auftrag „Ortsverzeichnis laden“ meldet sich über `auftraege` —
// dafür genügt der Stand (orteStandAuffrischen), die Familie folgt nur bei geändertem Verzeichnis.
const LAUF_FAMILIEN = ["lauf", "dokumente", "chargen", "masken", "pruefen", "kaestchen"] as const;
const DATEN_BEREICHE: readonly StandBereich[] = ["transkription", "kodierung", "bogen", "region"];

/** Die Präfixe, die ein `stand` mit diesen Bereichen ungültig macht (SPEC 03 §6.3 Tabelle). */
export function familienFuer(geaendert: readonly StandBereich[]): string[] {
  const aus = new Set<string>();
  if (geaendert.some((b) => DATEN_BEREICHE.includes(b))) DATEN_FAMILIEN.forEach((f) => aus.add(f));
  if (geaendert.includes("thema")) {
    DATEN_FAMILIEN.forEach((f) => aus.add(f));
    THEMA_FAMILIEN.forEach((f) => aus.add(f));
  }
  return [...aus];
}

/** Was am Stand des Ortsverzeichnisses die Karte/Listen ändert (keine Verortungszahlen). */
function verzeichnisSignatur(x: unknown): string {
  const s = x as OrteStand | undefined;
  if (!s) return "";
  return JSON.stringify([s.orte, s.strassen, s.nicht_oeffentlich, s.bezirke, s.aliase, s.schulen, (s.quellen ?? []).map((q) => [q.titel, q.abgerufen_am, q.in_gebrauch])]);
}

/** Befund F21: nur ["orte", V, "stand"] neu holen; hat sich das VERZEICHNIS geändert (Import,
 *  Alias, Ort), danach die ganze Familie (Karte, Listen, Suchen). */
export function orteStandAuffrischen(qc: QueryClient, V: string): void {
  const vorher = verzeichnisSignatur(qc.getQueryData(["orte", V, "stand"]));
  void Promise.resolve(qc.invalidateQueries({ queryKey: ["orte", V, "stand"], refetchType: "active" })).then(() => {
    const nachher = verzeichnisSignatur(qc.getQueryData(["orte", V, "stand"]));
    if (vorher && nachher && vorher !== nachher) void qc.invalidateQueries({ queryKey: ["orte", V], refetchType: "active" });
  });
}

/** Was am Stand des Ortsverzeichnisses Katalog und Kacheln der Auswertung ändert: Zahl der
 *  Stadtteile (Facette, Karten-Kachel) und Orte (die Karten-Kachel erscheint mit dem Verzeichnis).
 *  "" ohne brauchbaren Stand. */
export function auswertungsSignatur(x: unknown): string {
  const s = x as Partial<OrteStand> | null | undefined;
  if (!s || typeof s !== "object") return "";
  return `${s.bezirke?.genau ?? 0}/${s.bezirke?.ungefaehr ?? 0}/${s.orte ?? 0}`;
}

/** Wann der jüngste Auftrag „Ortsverzeichnis laden“ (Schritt `ortsimport`) fertig wurde; null,
 *  solange keiner in der aktuellen Welle fertig ist (oder der Stand unvollständig ist). */
export function ortsimportFertig(x: unknown): string | null {
  const gruppen = (x as Partial<AuftraegeStand> | null | undefined)?.gruppen;
  if (!Array.isArray(gruppen)) return null;
  return gruppen.find((g) => g?.schritt === "ortsimport")?.zuletzt_fertig ?? null;
}

export function invalidieren(qc: QueryClient, V: string, geaendert: readonly StandBereich[]): void {
  if (familienFuer(geaendert).length) orteStandAuffrischen(qc, V);
  for (const f of familienFuer(geaendert)) {
    // ["verfahren"] (Liste) hängt nicht an V, ["verfahren", V] schon — beide mitnehmen.
    if (f === "verfahren") void qc.invalidateQueries({ queryKey: ["verfahren"], exact: true, refetchType: "active" });
    // Beleg/Seite/Bogen/Dokument tragen kein V im Schlüssel — hier gilt die ganze Familie
    // (03 §6.2: ["dokument", ref] wie ["beleg", …]; Build-Review K11).
    const ohneV = f === "beleg" || f === "seite" || f === "bogen" || f === "dokument";
    void qc.invalidateQueries({ queryKey: ohneV ? [f] : [f, V], refetchType: "active" });
  }
}

export function useLive(V: string | null | undefined): void {
  const qc = useQueryClient();

  useEffect(() => {
    const setzeLive = useStore.getState().setzeLive;
    if (!V) {
      setzeLive({ seq: null, modus: "aus" });
      return;
    }
    const v = V;
    setzeLive({ seq: null, modus: "aus" });
    let es: EventSource | null = null;
    let fehlerFolge = 0;
    let abfrageUhr: ReturnType<typeof setInterval> | null = null;
    let wiederUhr: ReturnType<typeof setTimeout> | null = null;
    let verborgenUhr: ReturnType<typeof setTimeout> | null = null;
    let laufZuletzt = 0;
    let laufNachlauf: ReturnType<typeof setTimeout> | null = null;
    let laufAuchHolen = false;
    let aus = false;

    const seqJetzt = () => useStore.getState().live.seq;

    // Befund (Auswertung blieb nach einem Ortsimport bis zum Neuladen alt): bisher frischte nur
    // ein Effekt in „Orte“ Katalog und Kacheln auf — nur bei offenem Bildschirm, nie beim ersten
    // Öffnen. Jetzt hier, für jeden Bildschirm:
    //   · Stand des Verzeichnisses (["orte", V, "stand"], von wem auch immer geholt) mit anderer
    //     Signatur → Katalog und Kacheln ungültig;
    //   · `zuletzt_fertig` des Schritts `ortsimport` im Aufträge-Stand rückt vor (den hält die
    //     Kopfleiste und frischt der Strom bei `auftraege` auf) → dazu die Familie „orte“. Das
    //     greift auch, wenn gerade nur die Auswertung offen ist und niemand den Stand hält.
    // Die ersten Werte sind nur die Grundlage; ungültig wird erst eine Änderung.
    let orteBasis: string | null = auswertungsSignatur(qc.getQueryData(["orte", v, "stand"])) || null;
    const auftraegeDa = qc.getQueryData(["auftraege", v]);
    let importBasis: string | null | undefined = auftraegeDa === undefined ? undefined : ortsimportFertig(auftraegeDa);
    const verzeichnisNeu = (mitOrten: boolean) => {
      if (mitOrten) void qc.invalidateQueries({ queryKey: ["orte", v], refetchType: "active" });
      void qc.invalidateQueries({ queryKey: ["katalog", v], refetchType: "active" });
      void qc.invalidateQueries({ queryKey: ["kacheln", v], refetchType: "active" });
    };
    const abmelden = qc.getQueryCache().subscribe((ereignis) => {
      if (ereignis.type !== "updated" || ereignis.action.type !== "success") return;
      const k = ereignis.query.queryKey;
      if (k[1] !== v) return;
      const daten: unknown = ereignis.query.state.data;
      if (k[0] === "orte" && k[2] === "stand" && k.length === 3) {
        const jetzt = auswertungsSignatur(daten);
        if (!jetzt) return;
        if (orteBasis !== null && orteBasis !== jetzt) verzeichnisNeu(false);
        orteBasis = jetzt;
      } else if (k[0] === "auftraege" && k.length === 2) {
        const fertig = ortsimportFertig(daten);
        if (importBasis !== undefined && fertig !== null && fertig !== importBasis) verzeichnisNeu(true);
        // Fällt die Welle aus dem Stand (nach dem Nachlauf), bleibt der letzte Zeitpunkt die Grundlage.
        if (fertig !== null || importBasis === undefined) importBasis = fertig;
      }
    });

    const standVerarbeiten = (e: StandEreignis) => {
      const seq = seqJetzt();
      if (seq !== null && e.seq <= seq) return; // schon verarbeitet (Reconnect schickt stand erneut)
      useStore.getState().setzeLive({ seq: e.seq });
      if (e.geaendert?.length) invalidieren(qc, v, e.geaendert);
    };

    /** `mitLauf`: auch ["lauf", V] holen (nach `auftraege`; nach `lauf` ist es frisch gesetzt). */
    const laufInvalidieren = (mitLauf = false) => {
      if (mitLauf) laufAuchHolen = true;
      const jetzt = Date.now();
      const warten = LAUF_DROSSEL_MS - (jetzt - laufZuletzt);
      const los = () => {
        laufZuletzt = Date.now();
        laufNachlauf = null;
        const lauf = laufAuchHolen;
        laufAuchHolen = false;
        for (const f of LAUF_FAMILIEN) {
          // ["lauf", V] wurde eben frisch gesetzt — nicht gleich wieder holen.
          if (f === "lauf" && !lauf) continue;
          void qc.invalidateQueries({ queryKey: [f, v], refetchType: "active" });
        }
        orteStandAuffrischen(qc, v);
      };
      if (warten <= 0) los();
      else if (!laufNachlauf) laufNachlauf = setTimeout(los, warten);
    };

    const stoppeAbfrage = () => {
      if (abfrageUhr) clearInterval(abfrageUhr);
      if (wiederUhr) clearTimeout(wiederUhr);
      abfrageUhr = null;
      wiederUhr = null;
    };

    const schliessen = () => {
      if (es) {
        es.onopen = null;
        es.onerror = null;
        es.close();
      }
      es = null;
    };

    const abfragenEinmal = async () => {
      try {
        const e = await holeStand(v, seqJetzt() ?? undefined);
        if (!aus) standVerarbeiten(e);
      } catch {
        /* nächster Takt versucht es wieder */
      }
    };

    const abfragen = () => {
      if (aus) return;
      schliessen();
      stoppeAbfrage();
      useStore.getState().setzeLive({ modus: "abfrage" });
      abfrageUhr = setInterval(() => void abfragenEinmal(), ABFRAGE_MS);
      wiederUhr = setTimeout(() => {
        stoppeAbfrage();
        verbinden();
      }, SSE_WIEDER_MS);
    };

    function verbinden() {
      if (aus) return;
      schliessen();
      fehlerFolge = 0;
      let quelle: EventSource;
      try {
        quelle = new EventSource(liveUrl(v));
      } catch {
        abfragen();
        return;
      }
      es = quelle;
      quelle.onopen = () => {
        fehlerFolge = 0;
        useStore.getState().setzeLive({ modus: "live" });
      };
      quelle.addEventListener("stand", (m) => {
        fehlerFolge = 0;
        try {
          standVerarbeiten(JSON.parse((m as MessageEvent<string>).data) as StandEreignis);
        } catch {
          /* kaputter Rahmen — der nächste kommt */
        }
      });
      quelle.addEventListener("lauf", (m) => {
        fehlerFolge = 0;
        try {
          qc.setQueryData<LaufStand>(["lauf", v], JSON.parse((m as MessageEvent<string>).data) as LaufStand);
        } catch {
          return;
        }
        laufInvalidieren();
      });
      quelle.addEventListener("auftraege", () => {
        // Nutzlast ist leer oder nur Zahlen — der Knopf holt sich den Stand selbst.
        fehlerFolge = 0;
        void qc.invalidateQueries({ queryKey: ["auftraege", v], refetchType: "active" });
        laufInvalidieren(true);
      });
      quelle.addEventListener("fehler", () => {
        // Sitzung oder Zugriff beendet (02 §2.10.1): nicht wieder verbinden.
        schliessen();
        stoppeAbfrage();
        useStore.getState().setzeLive({ modus: "aus" });
        void qc.invalidateQueries({ queryKey: ["ich"] });
      });
      quelle.onerror = () => {
        // Build-Review K6: eine Nicht-200-Antwort (503 am Verbindungslimit, 404/401) schließt die
        // Quelle endgültig (CLOSED, genau ein error, kein Wiederverbinden) — dann sofort abfragen
        // (03 §6.3), sonst bliebe die Seite ohne Live und ohne Abfrage stehen.
        if (quelle.readyState === EventSource.CLOSED) {
          abfragen();
          return;
        }
        fehlerFolge += 1;
        if (fehlerFolge >= 2) abfragen();
      };
    }

    const sichtbarkeit = () => {
      if (document.visibilityState === "hidden") {
        if (verborgenUhr) clearTimeout(verborgenUhr);
        verborgenUhr = setTimeout(() => {
          verborgenUhr = null;
          schliessen();
          stoppeAbfrage();
        }, VERBORGEN_MS);
      } else {
        if (verborgenUhr) {
          clearTimeout(verborgenUhr);
          verborgenUhr = null;
          return; // war nicht lange weg — Verbindung steht noch
        }
        if (!es && !abfrageUhr) {
          void abfragenEinmal();
          verbinden();
        }
      }
    };
    document.addEventListener("visibilitychange", sichtbarkeit);

    verbinden();

    return () => {
      aus = true;
      abmelden();
      document.removeEventListener("visibilitychange", sichtbarkeit);
      schliessen();
      stoppeAbfrage();
      if (verborgenUhr) clearTimeout(verborgenUhr);
      if (laufNachlauf) clearTimeout(laufNachlauf);
      useStore.getState().setzeLive({ seq: null, modus: "aus" });
    };
  }, [V, qc]);
}
